import { Route } from 'vue-router';
import { Optional, TypedDict } from 'frontend/types';
import { LessonState } from 'frontend/types/lessons/lesson';
import { TranspilerWarning } from './warnings';
import {
  LegacyJavascriptInteractiveTypeName,
  JSONConfigInteractiveTypeName,
  Interactive,
  InteractiveTypeName,
  JSONConfigInteractive,
  JSON_CONFIG_TYPES,
  ALL_INTERACTIVE_TYPES,
  LegacyJavascriptInteractive,
  ElmInteractiveTypeName,
  LEGACY_JAVASCRIPT_TYPES,
  GENERIC_ELM_ASSET_TYPE_NAME,
} from 'frontend/types/interactiveTypes';
import { InteractivesManifest } from 'visualizations/shared/interactivesManager/types';

export type LessonAssetTypeName =
  | InteractiveTypeName
  | SampleDiagramTypeName
  | ImageTypeName
  | VideoTypeName;

export type LessonAsset =
  | ImageAsset
  | GenericElmAsset
  | JSONConfigAsset
  | LegacyJavascriptAsset
  | VideoAsset;

export type InteractiveAsset = GenericElmAsset | JSONConfigAsset | LegacyJavascriptAsset;

export type ImageTypeName = 'image';

export type VideoTypeName = 'video';

export type VideoBehavior = 'gif' | 'video';

export type SampleDiagramTypeName =
  | 'area-under-plot'
  | 'parametric-functions-ellipses'
  | 'slider-input-squircle'
  | 'histogram'
  | 'local-tangent'
  | 'canon-plot'
  | 'area-of-a-square'
  | 'spokes-on-a-wheel'
  | 'animated-angle'
  | 'stepped-solution'
  | 'planetary-gear'
  | 'boiling-water'
  | 'polarized-filters';

export interface Package {
  name: string;
  version: string;
  exposed: boolean;
}

/*
 * You may wonder, what's the difference between a BaseAsset and a BaseInteractive
 *
 * BaseAsset is the same, but includes the addition of:
 *   - Static items like Video and Image
 *   - Example versions of things (like the sample diagrams)
 *
 * There are some type level assumptions that work because
 * LessonAssetTypeName is a super set of InteractiveTypeName,
 * and if that assumption is violated, there will be cascading type
 * errors that fall out of that problem.
 *
 */
interface BaseAsset {
  alias: string;
  type: LessonAssetTypeName;
}

export interface ImageAsset extends BaseAsset {
  type: ImageTypeName;
  url: string;
  width: string;
  altText: string;
  figmaUrl?: string;
}

export interface VideoAsset extends BaseAsset {
  type: VideoTypeName;
  url: string;
  poster?: string;
  behavior: VideoBehavior;
}

export interface FigmaImage {
  url: string;
  slug: string;
  width: number;
  height: number;
  figmaUrl: string;
}

export interface ElmCompiledAsset extends BaseAsset {
  type: ElmInteractiveTypeName;
  compilationStatus?: CompilationStatus;
  elm: string;
  packages?: Package[];
  isSolvable?: boolean;
}

export type CompilationStatus = 'compiled' | 'failed_to_compile';

// tslint:disable-next-line:no-empty-interface
export interface JSONConfigAsset extends JSONConfigInteractive {}

export interface GenericElmAsset extends ElmCompiledAsset {
  type: typeof GENERIC_ELM_ASSET_TYPE_NAME;
}

// tslint:disable-next-line:no-empty-interface
export interface LegacyJavascriptAsset extends LegacyJavascriptInteractive {}

export interface LessonAssetVersion {
  lessonVersion: string;
  state: string;
  createdByName: string;
  ownedByName: string;
  createdTs: string;
  lastModifiedTs: string;
  internalChangeReason: string;
  asset: LessonAsset;
}

export type LessonAssetAliasMap = Record<string, LessonAssetVersion[]>;

export interface InteractiveSchema {
  [key: string]: any;
}
export type InteractiveSchemaDict = {
  [key in JSONConfigInteractiveTypeName | LegacyJavascriptInteractiveTypeName]: InteractiveSchema;
};

export function isElmAsset(asset: LessonAsset): asset is GenericElmAsset {
  return asset.type === GENERIC_ELM_ASSET_TYPE_NAME;
}

export function isLegacyJavascriptAsset(asset: LessonAsset): asset is LegacyJavascriptAsset {
  return asset.type in LEGACY_JAVASCRIPT_TYPES;
}

export function isJSONConfigAsset(
  asset: LessonAsset,
): asset is JSONConfigAsset | LegacyJavascriptInteractive {
  return asset.type in JSON_CONFIG_TYPES;
}

export function isInteractiveAsset(asset: LessonAsset): asset is InteractiveAsset {
  return ALL_INTERACTIVE_TYPES.hasOwnProperty(asset.type);
}
export function defaultInteractiveSelection(alias: string): Interactive {
  return {
    alias,
    type: 'parson',
    json: '{}',
  };
}

export function defaultGenericElmAssetSelection(alias: string): GenericElmAsset {
  return {
    alias,
    type: GENERIC_ELM_ASSET_TYPE_NAME,
    elm: '',
  };
}

export function defaultLegacyJavascriptSelection(alias: string): LegacyJavascriptAsset {
  return {
    alias,
    type: 'operatorsearch',
    json: '{}',
  };
}

export type ElmFileDict = TypedDict<string>;

export interface UsedAsset {
  alias: string;
  section: number; // line number of the start of the current section (0 for prelude)
  line: number; // line number where the alias is being referenced in the lesson source
}

export interface ContentHashes {
  blockHashes: TypedDict<string>;
  quizMemoryHashes: TypedDict<string>;
}

export interface LessonWidgetSchema {
  [key: string]: any;
}

export interface LessonSourceObject {
  content: {
    camperdown: string;
    assets: LessonAsset[];
  };
}

export const BLANK_LESSON_SOURCE: LessonSourceObject = {
  content: {
    camperdown: '',
    assets: [],
  },
};

export interface LessonSummary {
  version_id: number;
  version_name: string;
  internal_change_reason: string;
  state: string;
  version_state: LessonState;
  created_ts: string;
  last_modified_ts: string;
  created_by_name: string;
  owned_by_name: string;
  owned_by_id: number;
  lesson_url: string;
  lesson_title: string;
  edit_url: string;
  lesson_source: Optional<LessonSourceObject>;
  // Not always included, maybe should move to a separate type
  parent_version_name?: string;
  latest_autosave_ts?: string;
  latest_autosave_by_name?: string;
  latest_autosave_by_id?: number;
  approx_num_users?: string;
  lesson_report_count?: number;
  likes?: number;
  dislikes?: number;
}

export interface CourseTitlesItem {
  title: string;
  versions_url: string;
}

export interface ExtendedLessonSummary extends LessonSummary {
  image: string;
  lesson_info_admin_url: string;
  lesson_info_id: number;
  courses: CourseTitlesItem[];
}

export interface LessonAutosave {
  autosave_id: number;
  lesson_source: LessonSourceObject;
  created_by: string;
  created_ts: string;
}

export interface LessonAutosaveSummary {
  autosave_id: number;
  created_by: string;
  created_ts: string;
}

export interface LessonHistoryItem {
  item_type: 'autosave' | 'version';
  version_name: string;
  owned_by: string;
  last_modified_ts: string;
  internal_change_reason: string;
  lesson_source: LessonSourceObject;
}

export const WARNING_GUTTER_MARKER_KEY = 'syntax-warning-gutter';
export const ELM_ERROR_MARK_CLASS_NAME = 'elm-error-highlight';

export type EditorView = 'lesson' | 'assets' | 'settings' | 'compiledElmMain';

export const DEFAULT_EDITOR_VIEW: EditorView = 'lesson';

export interface ParsedEditorHashPath {
  view: EditorView;
  subview?: string;
  highlightLines?: [number, number];
}

export interface SelectorGroup<T> {
  group_name: string;
  items: T[];
}

export type SelectorIndex = [number, number];

export type EditorStatusType = 'STATUS' | 'ERROR' | 'SUCCESS';
export type EditorStatusTuple = [string, EditorStatusType];

export interface TranspilerResponse {
  mainElm: string;
  additionalElm: ElmFileDict;
  additionalMains: string[];
  packages: Package[];
  contentHashes: ContentHashes;
  lessonWidgetSchema: LessonWidgetSchema[];
  unifiedWarnings: TranspilerWarning[];
  interactivesManifest: InteractivesManifest;
  usedAssets: UsedAsset[];
}

export type SidebarTab = 'live_preview' | 'feedback' | 'snippets';

export interface OptionsLocalStorage {
  renderMathInLivePreview: boolean;
  isSidebarExpanded: boolean;
  currentSidebarTab: SidebarTab;
}

export interface CompiledElmObject {
  elm_main_module: string;
  additional_main_modules: string[];
  elm_files: ElmFileDict;
}

export type CompiledElmGetter = (route: Route) => CompiledElmObject;

export interface LessonLocalStorage {
  lessonSource: LessonSourceObject;
  localStorageTimestamp: number;
}

export type AssetCreationCategory = 'diagrams' | 'interactives';

// This should align with ReportType in brilliant/courses/lessonreports/types.py
export type LessonReportType =
  | 'TYPO'
  | 'VISUALS_INTERACTIVES_BROKEN'
  | 'DISPUTE'
  | 'TERM_HELP'
  | 'CLARIFICATION'
  | 'TOO_FAST'
  | 'TOO_SLOW';

export interface LessonReport {
  report_id: number;
  submitted_by: string;
  created_ts: string;
  user_id: number;
  profile_url: string;
  profile_url_part: string;
  is_staff: boolean;
  block: number;
  type: LessonReportType;
  contents: string;
}

export interface LessonFeedback {
  feedback_id: number;
  submitted_by: string;
  created_ts: string;
  user_id: number;
  profile_url: string;
  profile_url_part: string;
  is_staff: boolean;
  liked: boolean;
  contents: string;
}
